import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ThemeToggle from '../ThemeToggle';

const Header = () => {
  // useAuth must be called unconditionally (Rules of Hooks).
  // In test/edge scenarios the AuthProvider may be missing; instead of breaking the app, fail-soft.
  let user = null;
  let logout = null;
  try {
    const auth = useAuth();
    user = auth?.user ?? null;
    logout = auth?.logout ?? null;
  } catch (e) {
    user = null;
    logout = null;
  }


  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="cf-surface shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#00CED1] to-[#1E90FF] flex items-center justify-center">
              <i className="fas fa-briefcase text-white text-sm"></i>
            </div>
            <Link to="/" className="text-xl font-bold font-lexend cf-text">
              CareerFlow.io
            </Link>
          </div>

          {/* Mobile: show menu trigger (drawer/dropdown) */}
          <div className="md:hidden relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`flex items-center space-x-1 text-gray-600 hover:text-[#00CED1] font-medium transition-colors duration-300 focus:outline-none ${isMenuOpen ? 'text-[#00CED1]' : ''
                }`}
              aria-label="Open menu"
            >
              <i className="fas fa-bars" />
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 mt-3 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fade-in-up transform origin-top-right z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Career Tools</p>
                </div>
                <Link
                  to="/ai-assistants"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-magic mr-3 w-5 text-center" />AI Writing Assistants
                </Link>
                <Link
                  to="/resume-templates"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-file-invoice mr-3 w-5 text-center" />Resume Templates
                </Link>
                <Link
                  to="/cover-letter"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-envelope-open-text mr-3 w-5 text-center" />Cover Letter Generator
                </Link>
                <Link
                  to="/cover-letter-templates"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-file-alt mr-3 w-5 text-center" />Cover Letter Templates
                </Link>
                <Link
                  to="/interview/mock"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-user-clock mr-3 w-5 text-center" />Mock Scheduler
                </Link>
                <Link
                  to="/interview/prep"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-book-reader mr-3 w-5 text-center" />Prep Notes
                </Link>
                <Link
                  to="/salary"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-chart-pie mr-3 w-5 text-center" />Salary Insights
                </Link>
                <Link
                  to="/community"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-users mr-3 w-5 text-center" />Community
                </Link>
                <Link
                  to="/smart-jobs"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-briefcase mr-3 w-5 text-center" />Smart Job Manager
                </Link>
                <Link
                  to="/messages"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-comments mr-3 w-5 text-center" />Messages
                </Link>
                <Link
                  to="/jobs"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-briefcase mr-3 w-5 text-center" />Job Postings
                </Link>
                <Link
                  to="/companies"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-building mr-3 w-5 text-center" />Company Tracker
                </Link>
                <Link
                  to="/skill-assessment"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-award mr-3 w-5 text-center" />Skill Assessment
                </Link>
              </div>
            )}
          </div>

          <nav className="hidden md:flex items-center space-x-6">
            {user && user.role === 'employer' && (
              <Link
                to="/employer/dashboard"
                className={`main-nav-link text-gray-600 hover:text-[#00CED1] font-medium border-b-2 border-transparent transition-colors duration-300 ${isActive('/employer/dashboard') ? 'text-[#00CED1] border-[#00CED1]' : ''
                  }`}
              >
                <i className="fas fa-briefcase mr-2"></i>Employer Dashboard
              </Link>
            )}

            {user && user.role !== 'employer' && (
              <>
                <Link
                  to="/builder"
                  className={`main-nav-link text-gray-600 hover:text-[#00CED1] font-medium border-b-2 border-transparent transition-colors duration-300 ${isActive('/builder') ? 'text-[#00CED1] border-[#00CED1]' : ''
                    }`}
                >
                  <i className="fas fa-file-alt mr-2"></i>Resume Builder
                </Link>
                <Link
                  to="/tracker"
                  className={`main-nav-link text-gray-600 hover:text-[#00CED1] font-medium border-b-2 border-transparent transition-colors duration-300 ${isActive('/tracker') ? 'text-[#00CED1] border-[#00CED1]' : ''
                    }`}
                >
                  <i className="fas fa-tasks mr-2"></i>Job Tracker
                </Link>
                <Link
                  to="/analytics"
                  className={`main-nav-link text-gray-600 hover:text-[#00CED1] font-medium border-b-2 border-transparent transition-colors duration-300 ${isActive('/analytics') ? 'text-[#00CED1] border-[#00CED1]' : ''
                    }`}
                >
                  <i className="fas fa-chart-line mr-2"></i>Analytics
                </Link>
                <Link
                  to="/resume"
                  className={`main-nav-link text-gray-600 hover:text-[#00CED1] font-medium border-b-2 border-transparent transition-colors duration-300 ${isActive('/resume') ? 'text-[#00CED1] border-[#00CED1]' : ''
                    }`}
                >
                  <i className="fas fa-user mr-2"></i>Your Resumes
                </Link>

                {/* Menu Button Dropdown */}
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className={`flex items-center space-x-1 text-gray-600 hover:text-[#00CED1] font-medium transition-colors duration-300 focus:outline-none ${isMenuOpen ? 'text-[#00CED1]' : ''
                      }`}
                  >
                    <i className="fas fa-bars mr-2"></i>
                    <span>Menu</span>
                    <i className={`fas fa-chevron-down text-xs transition-transform duration-200 ${isMenuOpen ? 'transform rotate-180' : ''}`}></i>
                  </button>

                  {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-fade-in-up transform origin-top-right">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Career Tools</p>
                      </div>
                      <Link to="/ai-assistants" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-magic mr-3 w-5 text-center"></i>AI Writing Assistants
                      </Link>

                      <Link to="/resume-templates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-file-invoice mr-3 w-5 text-center"></i>Resume Templates
                      </Link>
                      <Link to="/cover-letter" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-envelope-open-text mr-3 w-5 text-center"></i>Cover Letter Generator
                      </Link>
                      <Link to="/cover-letter-templates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-file-alt mr-3 w-5 text-center"></i>Cover Letter Templates
                      </Link>
                      <Link to="/interview/mock" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-user-clock mr-3 w-5 text-center"></i>Mock Scheduler
                      </Link>
                      <Link to="/interview/prep" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-book-reader mr-3 w-5 text-center"></i>Prep Notes
                      </Link>
                      <Link to="/salary" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-chart-pie mr-3 w-5 text-center"></i>Salary Insights
                      </Link>
                      <Link to="/community" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-users mr-3 w-5 text-center"></i>Community
                      </Link>
                      <Link to="/smart-jobs" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-briefcase mr-3 w-5 text-center"></i>Smart Job Manager
                      </Link>
                      <Link to="/messages" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-comments mr-3 w-5 text-center"></i>Messages
                      </Link>
                      <Link to="/jobs" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-briefcase mr-3 w-5 text-center"></i>Job Postings
                      </Link>

                      <Link to="/companies" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-building mr-3 w-5 text-center"></i>Company Tracker
                      </Link>
                      <Link to="/skill-assessment" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-award mr-3 w-5 text-center"></i>Skill Assessment
                      </Link>

                      <div className="border-t border-gray-100 my-1"></div>

                      <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#00CED1] transition-colors" onClick={() => setIsMenuOpen(false)}>
                        <i className="fas fa-id-card mr-3 w-5 text-center"></i>Profile
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>

          <div className="flex items-center space-x-4">
            <ThemeToggle />
            {user ? (
              <>
                <Link
                  to={user.role === 'employer' ? "/employer/dashboard" : "/dashboard"}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Dashboard"
                >
                  <i className="fas fa-home-alt text-gray-500"></i>
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    title="Admin Dashboard"
                  >
                    <i className="fas fa-user-shield text-purple-500"></i>
                  </Link>
                )}
                <div className="hidden md:block h-6 border-r border-gray-300"></div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">Hi, {user.name}</span>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 rounded-lg text-gray-600 font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div className="flex space-x-3">
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-lg text-gray-600 font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 rounded-lg text-white font-semibold shadow-md bg-gradient-to-r from-[#00CED1] to-[#1E90FF] hover:shadow-lg transition-all duration-300"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
